// Alive Water - "Where we deliver" page (sections/aw-delivery-areas.liquid). Loaded by that section only.
//
// SOURCE OF TRUTH: the yes/no verdict and the delivery frequency come ONLY from the same theme assets the ZIP
// gate reads (aw-served-zips.json, aw-surcharge-zips.json, aw-zip-regions.json, aw-zip-notes.json), resolved
// by resolve() below, which mirrors resolve() in aw-zip-gate.js line for line. The page therefore cannot say
// yes where the gate says no. aw-delivery-areas-map.json only NAMES and DRAWS areas; a ZIP it knows but the
// served list does not is still a no.
//
// DAYS + CADENCE come from aw-zip-schedule.json through scheduleFor(), only for a ZIP that is already served.
// Days show only for areas marked confirmed and never for a ZIP in its noDays list; cadence follows the rule in
// scheduleFor(). If the schedule file fails to load, the page falls back to the website frequency only.
//
// The waitlist posts the gate's exact Klaviyo request (same endpoint, revision, list and body shape).
// Nothing else is written anywhere, except the gate's own localStorage handoff ("aw_delivery") when a
// visitor taps the order button, so the order page opens with their ZIP already checked.

// ---------- pure logic (exported for the QA harness; mirrors aw-zip-gate.js) ----------
export function regionFor(zip, data) {
  const r = data.regions, key = r && r.zips && r.zips[zip];
  return (key && r.regions[key]) || null;
}
export function noteFor(zip, data) {
  const groups = data.notes;
  if (!Array.isArray(groups)) return null;
  for (const g of groups) if (g && g.zips && g.zips.indexOf(zip) > -1) return { note: g.note || '', cta: g.cta || '' };
  return null;
}
export function feeForMiles(miles, c) {
  if (miles == null || miles > c.maxMiles) return null;
  if (c.mode === 'flat') return c.flatFee;
  let fee = Math.ceil(miles / 5) * c.per5;
  if (c.feeCap) fee = Math.min(fee, c.feeCap);
  return fee;
}
export function resolve(zip, data, c) {
  const region = regionFor(zip, data);
  const cadence = region ? { label: region.label, weeks: region.weeks } : null;
  const note = noteFor(zip, data);
  if (data.served.has(zip)) return { state: 'served', zip, fee: 0, miles: 0, cadence, note };
  const miles = data.surcharge[zip], fee = feeForMiles(miles, c);
  if (fee === 0) return { state: 'served', zip, fee: 0, miles, cadence, note };
  if (fee != null) return { state: 'surcharge', zip, fee, miles, cadence, note };
  return { state: 'waitlist', zip, fee: null, miles: miles ?? null, cadence: null };
}
export function frequencyText(weeks) {
  const n = Number(weeks);
  if (!(n > 0)) return '';
  return n === 1 ? 'every week' : 'every ' + n + ' weeks';
}
// Delivery schedule for a SERVED ZIP. Never call it for an unserved ZIP; it does not decide coverage.
// Returns { days: [...], mode, weeks, conflict } or null when there is no schedule file.
//   days    only for a confirmed area, and never for a ZIP listed in noDays
//   weeks   null when the route map and the website regions file disagree for this ZIP (conflict);
//           otherwise the route-map weeks for a confirmed area (website weeks if the route map has none),
//           and the website weeks (or nothing) for an area that is not confirmed
export function scheduleFor(zip, sched, data) {
  if (!sched || !sched.zips || !sched.areas) return null;
  const id = sched.zips[zip], a = id ? sched.areas[id] : null;
  const region = data ? regionFor(zip, data) : null;
  const web = region && Number(region.weeks) > 0 ? Number(region.weeks) : null;
  if (!a) return { days: [], mode: 'none', weeks: web, conflict: false };
  const route = Number(a.weeks) > 0 ? Number(a.weeks) : null;
  const conflict = !!(web && route && web !== route);
  const weeks = conflict ? null : a.confirmed === true ? (route || web) : web;
  const noDays = sched._noDaysSet || (sched._noDaysSet = new Set(sched.noDays || []));
  const show = a.confirmed === true && Array.isArray(a.days) && a.days.length > 0 && !noDays.has(zip);
  return { days: show ? a.days.slice() : [], mode: show ? a.mode : 'none', weeks, conflict };
}
const DAY_NAMES = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
// "on Tuesdays and Fridays" (mode two), "on Thursdays or Fridays" (mode either), "on Mondays" (one)
export function daysText(days, mode) {
  const names = (days || []).map(d => (DAY_NAMES[d] || d) + 's');
  if (!names.length) return '';
  if (names.length === 1) return 'on ' + names[0];
  const joiner = mode === 'either' ? ' or ' : ' and ';
  return 'on ' + names.slice(0, -1).join(', ') + joiner + names[names.length - 1];
}
export function fill(tpl, vars) {
  return String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? String(vars[k]) : m));
}
const norm = s => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\bsaint\b/g, 'st').replace(/\bst\.?\s/g, 'st ').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
// Town lookup over SERVED ZIPs only. Returns { exact: [{town, st, zips}], near: [{town, st}] }.
export function findTown(query, map, servedSet, opts) {
  const q = norm(query), out = { exact: [], near: [] };
  if (q.length < 2 || !map) return out;
  const showOregon = !opts || opts.showOregon !== false;
  const byTown = new Map();
  for (const [z, rec] of Object.entries(map.zips)) {
    if (!servedSet.has(z)) continue;
    const [town, st] = map.cities[rec[1]].split('|');
    if (!showOregon && st === 'OR') continue;
    const k = town + '|' + st;
    if (!byTown.has(k)) byTown.set(k, { town, st, zips: [] });
    byTown.get(k).zips.push(z);
  }
  for (const t of byTown.values()) {
    const n = norm(t.town);
    if (n === q || norm(t.town + ' ' + t.st) === q) out.exact.push(t);
    else if (n.startsWith(q)) out.near.push({ town: t.town, st: t.st });
  }
  for (const t of out.exact) t.zips.sort();
  out.near.sort((a, b) => a.town.localeCompare(b.town));
  out.near = out.near.slice(0, 6);
  return out;
}

// ---------- page ----------
const SVGNS = 'http://www.w3.org/2000/svg';

function cfgFrom(el) {
  const d = el.dataset;
  return {
    servedUrl: d.servedUrl, surchargeUrl: d.surchargeUrl, regionsUrl: d.regionsUrl, notesUrl: d.notesUrl,
    mapUrl: d.mapUrl, scheduleUrl: d.scheduleUrl,
    mode: d.feeMode, flatFee: Number(d.flatFee), per5: Number(d.per5), maxMiles: Number(d.maxMiles),
    feeCap: Number(d.feeCap) || 0,
    klaviyoKey: d.klaviyoKey, klaviyoList: d.klaviyoList,
    showOregon: d.showOregon !== 'false', showMap: d.showMap !== 'false', handoff: d.handoff !== 'false'
  };
}

function track(name, params) {
  const payload = Object.assign({ gate: 'zip', location: 'delivery-areas' }, params || {});
  try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload)); } catch (e) {}
  try { if (typeof window.gtag === 'function') window.gtag('event', name, payload); } catch (e) {}
  try { if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload); } catch (e) {}
  try { const sa = window.Shopify && window.Shopify.analytics; if (sa && typeof sa.publish === 'function') sa.publish(name, payload); } catch (e) {}
}

// The gate's waitlist call, unchanged: same endpoint, revision header, list and profile properties.
export function waitlistRequest(zip, email, c) {
  const body = { data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: { email, properties: { waitlist_zip: zip, source: 'zip-gate waitlist' } } } } }, relationships: { list: { data: { type: 'list', id: c.klaviyoList } } } } };
  return {
    url: 'https://a.klaviyo.com/client/subscriptions/?company_id=' + c.klaviyoKey,
    init: { method: 'POST', headers: { 'Content-Type': 'application/json', revision: '2024-10-15' }, body: JSON.stringify(body) }
  };
}

function init(root) {
  const c = cfgFrom(root);
  const copy = (() => { try { return JSON.parse(root.querySelector('[data-awda-copy]').textContent); } catch (e) { return {}; } })();
  const $ = s => root.querySelector(s);
  const form = $('[data-awda-form]'), input = $('[data-awda-input]'), result = $('[data-awda-result]');
  const mapWrap = $('[data-awda-map]');
  let data = null, map = null, loading = null, mapLoading = null, current = null, sched = null, schedLoading = null;

  const json = url => fetch(url, { credentials: 'same-origin' }).then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  function loadData() {
    if (data) return Promise.resolve(data);
    if (loading) return loading;
    loading = Promise.all([
      json(c.servedUrl).catch(() => null), json(c.surchargeUrl).catch(() => null),
      c.regionsUrl ? json(c.regionsUrl).catch(() => null) : null, c.notesUrl ? json(c.notesUrl).catch(() => null) : null
    ]).then(([served, surcharge, regions, notes]) => {
      loading = null;
      if (!Array.isArray(served) || !surcharge || typeof surcharge !== 'object') return null;
      data = { served: new Set(served.map(String)), surcharge, regions, notes };
      return data;
    });
    return loading;
  }
  function loadSchedule() {
    if (sched || !c.scheduleUrl) return Promise.resolve(sched);
    if (schedLoading) return schedLoading;
    schedLoading = json(c.scheduleUrl).then(x => (sched = x)).catch(() => null).then(x => { schedLoading = null; return x; });
    return schedLoading;
  }
  function loadMap() {
    if (map) return Promise.resolve(map);
    if (mapLoading) return mapLoading;
    mapLoading = json(c.mapUrl).then(m => (map = m)).catch(() => null).then(m => { mapLoading = null; return m; });
    return mapLoading;
  }

  // ---- verdict rendering (textContent only; nothing from the network is ever parsed as HTML) ----
  function el(tag, cls, text) { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
  function show(nodes, cls) {
    result.replaceChildren(...nodes);
    result.className = 'awda__result ' + (cls || '');
    result.hidden = false;
  }
  function areaFor(zip) {
    if (!map || !map.zips[zip]) return null;
    const rec = map.zips[zip], a = map.areas[rec[0]];
    const [town, st] = map.cities[rec[1]].split('|');
    return { idx: rec[0], panel: a.p, name: a.c ? town + ', ' + st : a.n, town, st, x: rec[2], y: rec[3] };
  }
  function renderServed(res) {
    const area = areaFor(res.zip);
    const nodes = [el('p', 'awda__verdict', fill(copy.yes, { zip: res.zip }))];
    if (area) nodes.push(el('p', 'awda__area', area.name));
    if (res.state === 'surcharge') nodes.push(el('p', 'awda__line', fill(copy.fee, { fee: '$' + res.fee })));
    const sc = scheduleFor(res.zip, sched, data);
    const weeks = sc ? sc.weeks : (res.cadence ? res.cadence.weeks : null);
    const f = frequencyText(weeks);
    if (sc && sc.days.length) {
      const line = el('p', 'awda__line awda__days', fill(f ? copy.days : copy.daysOnly, { days: daysText(sc.days, sc.mode), frequency: f }));
      nodes.push(line);
      if (copy.window) nodes.push(el('p', 'awda__line awda__window', copy.window));
    } else if (f) {
      nodes.push(el('p', 'awda__line awda__freq', fill(copy.frequency, { frequency: f })));
    }
    if (res.note && res.note.note) nodes.push(el('p', 'awda__line', res.note.note + (res.note.cta ? ' ' + res.note.cta : '')));
    const cta = el('a', 'awda__btn awda__btn--primary', copy.cta);
    cta.href = root.dataset.ctaUrl || '/products/spring-water-delivery';
    cta.addEventListener('click', () => handoff(res));
    nodes.push(cta);
    show(nodes, 'is-yes');
    if (area) focusArea(area);
  }
  function renderWaitlist(zip) {
    const wrap = el('div', 'awda__waitlist');
    const id = 'awda-email-' + (root.id || 'x');
    const label = el('label', 'awda__vh', copy.emailLabel); label.htmlFor = id;
    const email = el('input', 'awda__input'); email.type = 'email'; email.id = id; email.autocomplete = 'email'; email.placeholder = copy.emailPlaceholder || 'you@example.com';
    const join = el('button', 'awda__btn', copy.join); join.type = 'button';
    const msg = el('p', 'awda__line'); msg.setAttribute('role', 'status'); msg.hidden = true;
    join.addEventListener('click', async () => {
      const v = (email.value || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { email.focus(); return; }
      if (!c.klaviyoKey || !c.klaviyoList) return;
      join.disabled = true;
      let ok = false;
      try { const rq = waitlistRequest(zip, v, c); const r = await fetch(rq.url, rq.init); ok = r.ok || r.status === 202; } catch (e) { ok = false; }
      join.disabled = false;
      msg.hidden = false;
      msg.textContent = ok ? fill(copy.joined, { zip }) : copy.joinError;
      if (ok) { email.hidden = true; join.hidden = true; }
      track('delivery_areas_waitlist_join', { result: ok ? 'ok' : 'error' });
    });
    const row = el('div', 'awda__row'); row.append(email, join);
    wrap.append(label, row, msg);
    show([el('p', 'awda__verdict', fill(copy.no, { zip })), el('p', 'awda__line', copy.noBody), wrap], 'is-no');
    clearFocus();
  }
  function renderTown(q, found) {
    const nodes = [];
    if (found.exact.length) {
      for (const t of found.exact) {
        nodes.push(el('p', 'awda__verdict', fill(copy.townYes, { town: t.town + ', ' + t.st })));
        if (t.zips.length <= 12) {
          const list = el('div', 'awda__chips');
          for (const z of t.zips) { const b = el('button', 'awda__chip', z); b.type = 'button'; b.addEventListener('click', () => { input.value = z; check(z); }); list.append(b); }
          nodes.push(el('p', 'awda__line', copy.townPick), list);
        } else {
          nodes.push(el('p', 'awda__line', fill(copy.townMany, { count: t.zips.length })));
        }
      }
      show(nodes, 'is-yes');
    } else if (found.near.length) {
      const list = el('div', 'awda__chips');
      for (const t of found.near) { const b = el('button', 'awda__chip', t.town + ', ' + t.st); b.type = 'button'; b.addEventListener('click', () => { input.value = t.town; check(t.town); }); list.append(b); }
      show([el('p', 'awda__line', copy.townNear), list], '');
    } else {
      show([el('p', 'awda__line', fill(copy.townUnknown, { query: q }))], 'is-no');
    }
    clearFocus();
  }
  function handoff(res) {
    if (!c.handoff) return;
    // same record the ZIP gate stores (v:1, 30-day expiry); the order page's gate re-checks it on load
    try { localStorage.setItem('aw_delivery', JSON.stringify(Object.assign({}, res, { v: 1, exp: Date.now() + 2592e6 }))); } catch (e) {}
  }

  async function check(raw) {
    const text = String(raw || '').trim();
    const digits = text.replace(/\D/g, '');
    const isZip = /^\d[\d\s-]*$/.test(text);
    if (!text) { input.focus(); return; }
    const [d] = await Promise.all([loadData(), loadMap(), loadSchedule()]);
    if (!d) { show([el('p', 'awda__line', copy.loadError)], 'is-no'); track('delivery_areas_check', { result: 'error' }); return; }
    if (isZip) {
      if (digits.length !== 5) { show([el('p', 'awda__line', copy.invalid)], 'is-no'); track('delivery_areas_check', { result: 'invalid' }); return; }
      const res = resolve(digits, d, c);
      current = res;
      if (res.state === 'waitlist') renderWaitlist(digits); else renderServed(res);
      track('delivery_areas_check', { result: res.state });
    } else {
      renderTown(text, findTown(text, map, d.served, { showOregon: c.showOregon }));
      track('delivery_areas_check', { result: 'town' });
    }
  }

  // clear the theme's overlay header (absolute over the top of the page) by its measured height
  function clearHeader() {
    const h = document.querySelector('.header-wrapper');
    if (!h) return;
    const pos = getComputedStyle(h).position;
    const px = pos === 'absolute' || pos === 'fixed' ? Math.ceil(h.getBoundingClientRect().height) : 0;
    root.style.setProperty('--awda-hdr', px + 'px');
  }
  clearHeader();
  window.addEventListener('load', clearHeader, { once: true });
  let rz = 0;
  window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(clearHeader, 150); });

  form.addEventListener('submit', e => { e.preventDefault(); check(input.value); });
  input.addEventListener('focus', () => { loadData(); loadMap(); loadSchedule(); }, { once: true });

  // ---- map: inline SVG, drawn once the map nears the viewport ----
  let panels = [], tabs = [], drawn = false;
  function drawMap(m) {
    if (drawn || !m || !mapWrap) return;
    drawn = true;
    const tabBar = mapWrap.querySelector('[data-awda-tabs]'), stage = mapWrap.querySelector('[data-awda-stage]');
    m.panels.forEach((p, pi) => {
      if (p.oregon && !c.showOregon) return;
      const t = el('button', 'awda__tab', p.name); t.type = 'button'; t.setAttribute('aria-pressed', 'false');
      t.addEventListener('click', () => selectPanel(pi));
      tabBar.append(t); tabs[pi] = t;
      const svg = document.createElementNS(SVGNS, 'svg');
      svg.setAttribute('viewBox', '0 0 ' + p.w + ' ' + p.h);
      svg.setAttribute('class', 'awda__svg' + (p.h > p.w * 1.4 ? ' is-tall' : ''));
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', fill(copy.mapAlt, { panel: p.name }));
      const st = document.createElementNS(SVGNS, 'path'); st.setAttribute('d', p.s); st.setAttribute('class', 'awda__state'); svg.append(st);
      let tone = 0;
      m.areas.forEach((a, ai) => {
        if (a.p !== pi || !a.d) return;
        const g = document.createElementNS(SVGNS, 'path'); g.setAttribute('d', a.d); g.setAttribute('class', 'awda__shape awda__shape--t' + (tone++ % 3)); g.dataset.area = ai;
        const tt = document.createElementNS(SVGNS, 'title'); tt.textContent = a.c ? '' : a.n; if (!a.c) g.append(tt);
        svg.append(g);
      });
      const pin = document.createElementNS(SVGNS, 'g'); pin.setAttribute('class', 'awda__pin'); pin.style.display = 'none';
      const ring = document.createElementNS(SVGNS, 'circle'); ring.setAttribute('r', '16'); ring.setAttribute('class', 'awda__pin-ring');
      const dot = document.createElementNS(SVGNS, 'circle'); dot.setAttribute('r', '7'); dot.setAttribute('class', 'awda__pin-dot');
      pin.append(ring, dot); svg.append(pin);
      const fig = el('div', 'awda__panel'); fig.hidden = true; fig.append(svg);
      const names = el('p', 'awda__names');
      m.areas.forEach(a => { if (a.p === pi && !a.c) names.append(el('span', 'awda__name', a.n)); });
      fig.append(names);
      stage.append(fig); panels[pi] = { fig, svg, pin };
    });
    const first = panels.findIndex(Boolean);
    selectPanel(current && areaFor(current.zip) ? areaFor(current.zip).panel : first);
    if (current && current.state !== 'waitlist') { const a = areaFor(current.zip); if (a) focusArea(a); }
  }
  function selectPanel(pi) {
    if (!panels[pi]) return;
    panels.forEach((p, i) => { if (!p) return; p.fig.hidden = i !== pi; tabs[i].setAttribute('aria-pressed', String(i === pi)); });
  }
  function clearFocus() {
    panels.forEach(p => { if (!p) return; p.pin.style.display = 'none'; p.svg.querySelectorAll('.is-on').forEach(n => n.classList.remove('is-on')); });
  }
  function focusArea(area) {
    if (!drawn) return;
    clearFocus();
    if (!panels[area.panel]) return; // e.g. Oregon hidden by the section setting: verdict still shows, map does not
    selectPanel(area.panel);
    const p = panels[area.panel];
    p.svg.querySelectorAll('[data-area="' + area.idx + '"]').forEach(n => n.classList.add('is-on'));
    if (area.x >= 0) { p.pin.setAttribute('transform', 'translate(' + area.x + ' ' + area.y + ')'); p.pin.style.display = ''; }
  }
  if (mapWrap && c.showMap) {
    const go = () => loadMap().then(drawMap);
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { io.disconnect(); go(); } }, { rootMargin: '400px 0px' });
      io.observe(mapWrap);
    } else go();
  }

  // ?zip=90405 or ?town=Santa%20Monica prefills and checks (for links from city pages and emails)
  try {
    const q = new URLSearchParams(location.search);
    const pre = q.get('zip') || q.get('town');
    if (pre) { input.value = pre.slice(0, 60); check(input.value); }
  } catch (e) {}
}

if (typeof document !== 'undefined') document.querySelectorAll('[data-awda]').forEach(init);
